import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { API_URL } from '../../config/api';
import { addNotification } from '../../store/slices/uiSlice';
import { fetchOrgWorkflowReactions } from '../../store/slices/workflowCatalogSlice';
import ConfirmModal from '../common/ConfirmModal';
import { WORKFLOW_REACTION_CATEGORIES } from '../../utils/reactionConfig';

const ADMIN_CATEGORIES = [
  { id: 'work-item-type', label: 'Type' },
  { id: 'workflow-status', label: 'Workflow status' },
  { id: 'priority', label: 'Priority' },
  { id: 'urgency', label: 'Urgency' },
  { id: 'severity', label: 'Severity' },
  { id: 'agile-stage', label: 'Agile stage' },
  { id: 'code-review', label: 'Code review' },
  { id: 'testing', label: 'Testing' },
  { id: 'build-deployment', label: 'Build' },
  { id: 'environment', label: 'Development' },
];

const TONES = ['slate', 'red', 'amber', 'green', 'blue', 'indigo', 'cyan', 'teal', 'violet'];
const SUGGESTED_EMOJIS = ['⭐', '📌', '🏷️', '✨', '🧭', '🧩', '🎯', '🧪', '🛠️', '🚀'];

const emptyDraft = () => ({
  category: 'work-item-type',
  label: '',
  emoji: '⭐',
  tone: 'slate',
});

const OrgWorkflowReactionsPanel = ({ token }) => {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/org/workflow-reactions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || 'Failed to load workflow tags',
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const grouped = useMemo(() => {
    const map = {};
    ADMIN_CATEGORIES.forEach((cat) => {
      const builtins = (WORKFLOW_REACTION_CATEGORIES.find((category) => category.id === cat.id)?.reactions || [])
        .map((reaction) => ({
          key: reaction.id,
          emoji: reaction.emoji,
          label: reaction.label,
          custom: false,
        }));
      const customs = (items || [])
        .filter((item) => item.category === cat.id)
        .map((item) => ({
          key: item.id,
          id: item.id,
          emoji: item.emoji,
          label: item.label,
          custom: true,
        }));
      map[cat.id] = [...builtins, ...customs];
    });
    return map;
  }, [items]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!draft.label.trim() || !draft.emoji.trim()) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/org/workflow-reactions`,
        draft,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems((prev) => [...prev, response.data]);
      setDraft((prev) => ({ ...emptyDraft(), category: prev.category }));
      dispatch(fetchOrgWorkflowReactions());
      dispatch(addNotification({ type: 'success', message: 'Workflow tag added' }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || 'Failed to add workflow tag',
      }));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/org/workflow-reactions/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.filter((item) => item.id !== deleteId));
      dispatch(fetchOrgWorkflowReactions());
      dispatch(addNotification({ type: 'success', message: 'Workflow tag removed' }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || 'Failed to delete workflow tag',
      }));
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Workflow tags</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Add custom Type, Status, Priority, and other tags. Built-in tags are listed below; custom ones also appear in the smile picker for everyone in this organization.
        </p>
      </div>

      <form onSubmit={handleCreate} className="p-6 border-b border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Group
          <select
            value={draft.category}
            onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))}
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2"
          >
            {ADMIN_CATEGORIES.map((category) => (
              <option key={category.id} value={category.id}>{category.label}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Name
          <input
            value={draft.label}
            onChange={(event) => setDraft((prev) => ({ ...prev, label: event.target.value }))}
            placeholder="e.g. Hotfix"
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2"
          />
        </label>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Emoji
          <input
            value={draft.emoji}
            onChange={(event) => setDraft((prev) => ({ ...prev, emoji: event.target.value }))}
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2"
          />
          <span className="mt-1 flex flex-wrap gap-1">
            {SUGGESTED_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => setDraft((prev) => ({ ...prev, emoji }))}
                className="px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {emoji}
              </button>
            ))}
          </span>
        </label>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Color
          <select
            value={draft.tone}
            onChange={(event) => setDraft((prev) => ({ ...prev, tone: event.target.value }))}
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2"
          >
            {TONES.map((tone) => (
              <option key={tone} value={tone}>{tone}</option>
            ))}
          </select>
        </label>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={saving || !draft.label.trim()}
            className="w-full px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add tag'}
          </button>
        </div>
      </form>

      <div className="p-6 space-y-5">
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          ADMIN_CATEGORIES.map((category) => {
            const tags = grouped[category.id] || [];
            const customCount = tags.filter((tag) => tag.custom).length;
            const builtinCount = tags.length - customCount;
            return (
            <div key={category.id}>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {category.label}
                <span className="ml-2 text-xs font-normal text-gray-400">
                  {builtinCount} built-in
                  {customCount ? ` · ${customCount} custom` : ''}
                </span>
              </h3>
              <ul className="mt-2 flex flex-wrap gap-2">
                {tags.map((item) => (
                  <li
                    key={item.key}
                    className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm ${
                      item.custom
                        ? 'border-primary-200 dark:border-primary-800 bg-primary-50/70 dark:bg-primary-950/30'
                        : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40'
                    }`}
                  >
                    <span>{item.emoji}</span>
                    <span className="font-medium text-gray-800 dark:text-gray-100">{item.label}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      {item.custom ? 'Custom' : 'Built-in'}
                    </span>
                    {item.custom ? (
                      <button
                        type="button"
                        onClick={() => setDeleteId(item.id)}
                        className="text-[11px] font-semibold text-rose-600 hover:text-rose-700"
                      >
                        Remove
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
            );
          })
        )}
      </div>

      <ConfirmModal
        isOpen={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title="Remove workflow tag"
        message="People can still see this tag on old messages, but it will no longer show in the smile picker."
        confirmText="Remove"
        type="danger"
      />
    </div>
  );
};

export default OrgWorkflowReactionsPanel;
